import { DeleteSmall, StatusAlert } from '@wix/wix-ui-icons-common';
import React, { type ReactNode, useRef, useEffect } from 'react';
import { Button as ButtonRAC } from 'react-aria-components';

import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { useTranslation } from '../../../contexts/translation-context';
import {
  applyInternalProps,
  findChildOfType,
} from '../../../services/find-child';
import { type FileUploadProps } from '../file-upload-props';
import { type FileUploadFile } from '../use-file-upload-controller';

import { useFileUploadContext } from './file-upload-context';
import { ProgressCircle } from './progress-circle';

export type FileProps = {
  children: ReactNode;
  className?: string;
};

export type FileInternalProps = {
  file: FileUploadFile;
  index: number;
  focusFile: boolean;
};

export const File = ({ children, className, ...internal }: FileProps) => {
  const { file, index, focusFile } = internal as FileInternalProps;
  const fileName = file.file.name;

  const nameElement = findChildOfType(children, File.Name);
  const removeElement = findChildOfType(children, File.Remove);
  const spinnerElement = findChildOfType(children, File.Spinner);
  const errorElement = findChildOfType(children, File.Error);

  return (
    <div className={className}>
      {applyInternalProps<FileNameInternalProps>(nameElement, { fileName })}

      {file.state === 'uploading'
        ? spinnerElement
        : applyInternalProps<FileRemoveInternalProps>(removeElement, {
            index,
            fileName,
            focused: focusFile,
          })}

      {file.errorMessage &&
        applyInternalProps<FileErrorInternalProps>(errorElement, {
          errorMessage: file.errorMessage,
        })}
    </div>
  );
};

export type FileNameProps = {
  className?: string;
};

export type FileNameInternalProps = {
  fileName: string;
};

export const FileName = ({ className, ...internal }: FileNameProps) => {
  const { fileName } = internal as FileNameInternalProps;

  return <span className={className}>{fileName}</span>;
};

export type FileRemoveProps = {
  className?: string;
};

export type FileRemoveInternalProps = {
  index: number;
  fileName: string;
  focused: boolean;
};

export const FileRemove = ({ className, ...internal }: FileRemoveProps) => {
  const { t } = useTranslation();
  const { fileName, index, focused } = internal as FileRemoveInternalProps;
  const { disabled } = useFieldPropsV2<FileUploadProps>();
  const { handleDelete, fileRemoveButtonRefs } = useFileUploadContext();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const refs = fileRemoveButtonRefs.current;
    const fileKey = fileName;
    refs[fileKey] = buttonRef;

    return () => {
      if (refs[fileKey] === buttonRef) {
        refs[fileKey] = undefined;
      }
    };
  }, [fileName, fileRemoveButtonRefs]);

  useEffect(() => {
    if (focused) {
      buttonRef.current?.focus();
    }
  }, [focused]);

  const handleClick = () => {
    handleDelete(index);
  };

  return (
    <ButtonRAC
      ref={buttonRef}
      aria-disabled={disabled}
      aria-label={t('file-upload.delete-file.aria-label')}
      aria-description={fileName}
      onClick={handleClick}
      className={className}
      isDisabled={disabled}
    >
      <DeleteSmall />
    </ButtonRAC>
  );
};

export type FileErrorProps = {
  className?: string;
};

export type FileErrorInternalProps = {
  errorMessage?: string;
};

export const FileError = ({ className, ...internal }: FileErrorProps) => {
  const { errorMessage } = internal as FileErrorInternalProps;

  if (!errorMessage) {
    return null;
  }

  return (
    <div className={className} role="alert">
      <StatusAlert aria-hidden="true" />

      {errorMessage}
    </div>
  );
};

export type FileSpinnerProps = {
  className?: string;
};

export const FileSpinner = ({ className }: FileSpinnerProps) => {
  const { t } = useTranslation();
  return (
    <ProgressCircle
      aria-label={t('file-upload.file.uploading-spinner.aria-label')}
      className={className}
    />
  );
};

File.Name = FileName;
File.Remove = FileRemove;
File.Spinner = FileSpinner;
File.Error = FileError;
